import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Ban, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { NumberInput } from '@/components/ui/NumberInput';
import { Spinner } from '@/components/ui/Spinner';
import {
  cancelOrder,
  changeOrderPaymentMethod,
  deleteOrder,
  getOrder,
  listOrders,
  type ChangePaymentBody,
  type OrderDetail,
} from '@/services/admin';
import { formatDate, formatRub, orderStatusLabels, paymentMethodLabels } from '@/utils/format';

export default function OrdersPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/orders', { status, source, q }],
    queryFn: () =>
      listOrders({
        status: status || undefined,
        source: source || undefined,
        q: q || undefined,
      }),
    refetchInterval: 15_000,
  });

  const { data: orderFull } = useQuery({
    queryKey: ['admin/order', openId],
    queryFn: () => getOrder(openId!),
    enabled: !!openId,
  });

  const cancelMut = useMutation({
    mutationFn: cancelOrder,
    onSuccess: () => {
      toast.success('Заказ отменён');
      qc.invalidateQueries({ queryKey: ['admin/orders'] });
      qc.invalidateQueries({ queryKey: ['admin/order'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteOrder,
    onSuccess: (r) => {
      toast.success(`Заказ №${r.number} удалён`);
      setOpenId(null);
      qc.invalidateQueries({ queryKey: ['admin/orders'] });
    },
  });

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Заказы</h1>
          <p className="text-sm text-slate-500">Полный список заказов (касса + приложение)</p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
        <select className="input w-[calc(50%-0.25rem)] sm:w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Все статусы</option>
          {['NEW', 'IN_PROGRESS', 'READY', 'COMPLETED', 'CANCELLED'].map((s) => (
            <option key={s} value={s}>{orderStatusLabels[s]}</option>
          ))}
        </select>
        <select className="input w-[calc(50%-0.25rem)] sm:w-44" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Все источники</option>
          <option value="CASH">Касса</option>
          <option value="APP">Приложение</option>
        </select>
        <input className="input flex-1 min-w-[180px]" placeholder="Поиск по номеру/клиенту"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">№</th>
                <th className="text-left px-4 py-3">Создан</th>
                <th className="text-left px-4 py-3">Стол / Клиент</th>
                <th className="text-left px-4 py-3">Источник</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="text-right px-4 py-3">Сумма</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((o) => (
                <tr key={o.id} className="table-row">
                  <td className="px-4 py-3 font-medium">#{o.number}</td>
                  <td className="px-4 py-3">{formatDate(o.createdAt)}</td>
                  <td className="px-4 py-3">
                    {o.table ? `${o.table.area === 'TAKEAWAY' ? 'Навынос' : 'Стол'} ${o.table.number}` :
                      o.client?.name ?? o.client?.phone ?? 'Без стола'}
                  </td>
                  <td className="px-4 py-3">
                    {o.source === 'CASH' ? <Badge>Касса</Badge> : <Badge tone="info">Приложение</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{formatRub(o.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost" onClick={() => setOpenId(o.id)}>
                      <Eye size={16} />
                    </button>
                    {o.status !== 'CANCELLED' && o.status !== 'COMPLETED' && (
                      <button className="btn-ghost text-amber-600" title="Отменить заказ" onClick={() => {
                        if (confirm('Отменить заказ?')) cancelMut.mutate(o.id);
                      }}>
                        <Ban size={16} />
                      </button>
                    )}
                    <button
                      className="btn-ghost text-red-600 disabled:text-slate-300 disabled:cursor-not-allowed"
                      title={
                        o.canDelete
                          ? 'Удалить заказ безвозвратно'
                          : 'Заказ из закрытой смены — удалять нельзя, используйте «Отменить»'
                      }
                      disabled={deleteMut.isPending || !o.canDelete}
                      onClick={() => {
                        if (!o.canDelete) return;
                        if (
                          confirm(
                            `Удалить заказ #${o.number} безвозвратно?\n\n` +
                              'Будут удалены позиции и платежи заказа, он исчезнет из отчётов и выручки. ' +
                              'Действие нельзя отменить.',
                          )
                        )
                          deleteMut.mutate(o.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Заказов нет</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!openId}
        onClose={() => setOpenId(null)}
        title={orderFull ? `Заказ №${orderFull.number}` : 'Заказ'}
        size="lg"
      >
        {orderFull ? <OrderDetails order={orderFull} /> : <Spinner />}
      </Modal>
    </div>
  );
}

// Способы, на которые можно поменять оплату (п.1). ONLINE (ЮKassa) — нельзя:
// деньги реально прошли через эквайринг, бэкенд вернёт 409.
const CHANGEABLE_METHODS = ['cash', 'card', 'transfer', 'qrcode'] as const;
type ChangeableMethod = (typeof CHANGEABLE_METHODS)[number];

function OrderDetails({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const paid = order.payments.filter((p) => p.status === 'PAID');
  const paidTotal = paid.reduce((s, p) => s + p.amount, 0);
  const hasOnline = paid.some((p) => p.method === 'ONLINE');
  const canChange = order.status !== 'CANCELLED' && paid.length > 0 && !hasOnline;

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<'single' | 'split'>('single');
  const [method, setMethod] = useState<ChangeableMethod>('cash');
  // Смешанная оплата — суммы в РУБЛЯХ для ввода, в копейки переводим при отправке.
  const [split, setSplit] = useState<Record<ChangeableMethod, number>>({
    cash: 0,
    card: 0,
    transfer: 0,
    qrcode: 0,
  });

  const changeMut = useMutation({
    mutationFn: (b: ChangePaymentBody) => changeOrderPaymentMethod(order.id, b),
    onSuccess: () => {
      toast.success('Способ оплаты изменён');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['admin/order', order.id] });
      qc.invalidateQueries({ queryKey: ['admin/orders'] });
      // Итоги смены/отчёты считаются по способам — сбрасываем их кэш тоже.
      qc.invalidateQueries({ queryKey: ['admin/current-shift'] });
      qc.invalidateQueries({ queryKey: ['admin/shifts'] });
    },
  });

  const startEditing = () => {
    // Предзаполняем текущим состоянием: один способ → single, несколько → split.
    if (paid.length === 1 && CHANGEABLE_METHODS.includes(paid[0].method.toLowerCase() as ChangeableMethod)) {
      setMode('single');
      setMethod(paid[0].method.toLowerCase() as ChangeableMethod);
    } else {
      setMode('split');
    }
    const next: Record<ChangeableMethod, number> = { cash: 0, card: 0, transfer: 0, qrcode: 0 };
    for (const p of paid) {
      const m = p.method.toLowerCase() as ChangeableMethod;
      if (CHANGEABLE_METHODS.includes(m)) next[m] += p.amount / 100;
    }
    setSplit(next);
    setEditing(true);
  };

  const splitKop = Object.fromEntries(
    CHANGEABLE_METHODS.map((m) => [m, Math.round((split[m] || 0) * 100)]),
  ) as Record<ChangeableMethod, number>;
  const splitSum = CHANGEABLE_METHODS.reduce((s, m) => s + splitKop[m], 0);
  const splitOk = splitSum === paidTotal && splitSum > 0;

  const submit = () => {
    if (mode === 'single') {
      changeMut.mutate({ method });
      return;
    }
    if (!splitOk) return;
    const body: ChangePaymentBody['split'] = {};
    for (const m of CHANGEABLE_METHODS) if (splitKop[m] > 0) body[m] = splitKop[m];
    changeMut.mutate({ split: body });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs text-slate-500">Создан</div>
          <div className="font-medium">{formatDate(order.createdAt)}</div>
        </div>
        <StatusBadge status={order.status} />
      </div>
      {order.client && (
        <div>
          <div className="text-xs text-slate-500">Клиент</div>
          <div>{order.client.name ?? '—'} · {order.client.phone ?? '—'}</div>
        </div>
      )}
      {order.table && (
        <div className="text-sm">
          {order.table.area === 'TAKEAWAY' ? 'Навынос' : 'Стол'} {order.table.number}
        </div>
      )}
      {(order.clientPhone || order.deliveryAddress) && (
        <div className="rounded-lg bg-slate-50 px-3 py-2 space-y-1">
          {order.clientPhone && (
            <div className="text-sm">
              <span className="text-xs text-slate-500">Телефон:&nbsp;</span>
              {order.clientPhone}
            </div>
          )}
          {order.deliveryAddress && (
            <div className="text-sm">
              <span className="text-xs text-slate-500">Адрес:&nbsp;</span>
              {order.deliveryAddress}
            </div>
          )}
        </div>
      )}
      <div>
        <div className="text-sm font-semibold mb-2">Состав</div>
        <div className="divide-y divide-slate-100">
          {order.items.map((i) => (
            <div key={i.id} className="py-2 flex justify-between items-start text-sm">
              <div>
                <div className="font-medium">{i.name} × {i.quantity}</div>
                {i.modifiers.length > 0 && (
                  <div className="text-xs text-slate-500">
                    {i.modifiers.map((m) => m.name).join(', ')}
                  </div>
                )}
                {i.comment && <div className="text-xs text-slate-400">{i.comment}</div>}
              </div>
              <div className="text-right">
                <div>{formatRub(i.total)}</div>
                <div className="text-xs text-slate-400">{formatRub(i.unitPrice)} / шт</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-3 border-t border-slate-100 space-y-1 text-sm">
        <div className="flex justify-between"><span>Подытог</span><span>{formatRub(order.subtotal)}</span></div>
        {order.discount > 0 && (
          <div className="flex justify-between text-amber-700"><span>Скидка</span><span>−{formatRub(order.discount)}</span></div>
        )}
        <div className="flex justify-between font-semibold text-base pt-1"><span>Итого</span><span>{formatRub(order.total)}</span></div>
      </div>
      {order.payments.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold">Оплаты</div>
            {canChange && !editing && (
              <button type="button" className="btn-ghost text-xs" onClick={startEditing}>
                Изменить способ оплаты
              </button>
            )}
          </div>
          <div className="text-sm">
            {order.payments.map((p, i) => (
              <div
                key={p.id ?? i}
                className={`flex justify-between ${p.status !== 'PAID' ? 'text-slate-400 line-through' : ''}`}
              >
                <span>{paymentMethodLabels[p.method] ?? p.method}</span>
                <span>
                  {formatRub(p.amount)} · {PAYMENT_STATUS_LABELS[p.status] ?? p.status}
                </span>
              </div>
            ))}
          </div>
          {hasOnline && (
            <p className="text-xs text-slate-400 mt-1">
              Онлайн-оплату через ЮKassa изменить нельзя — деньги прошли через эквайринг.
            </p>
          )}
          {editing && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              {order.shift?.status === 'CLOSED' && (
                <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Смена №{order.shift.number} уже закрыта: её итоги по способам оплаты
                  изменятся задним числом. Меняйте только если уверены.
                </div>
              )}
              <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 w-fit text-xs">
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md ${mode === 'single' ? 'bg-brand-600 text-white' : 'text-slate-600'}`}
                  onClick={() => setMode('single')}
                >
                  Один способ
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 rounded-md ${mode === 'split' ? 'bg-brand-600 text-white' : 'text-slate-600'}`}
                  onClick={() => setMode('split')}
                >
                  Смешанная
                </button>
              </div>
              {mode === 'single' ? (
                <div>
                  <label className="label">Новый способ оплаты · {formatRub(paidTotal)}</label>
                  <select
                    className="input"
                    value={method}
                    onChange={(e) => setMethod(e.target.value as ChangeableMethod)}
                  >
                    {CHANGEABLE_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {paymentMethodLabels[m.toUpperCase()] ?? m}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {CHANGEABLE_METHODS.map((m) => (
                    <div key={m}>
                      <label className="label">{paymentMethodLabels[m.toUpperCase()] ?? m}, ₽</label>
                      <NumberInput
                        className="input"
                        value={split[m]}
                        zeroAsPlaceholder
                        onChange={(n) => setSplit({ ...split, [m]: Math.max(0, n) })}
                      />
                    </div>
                  ))}
                  <p className={`col-span-2 text-xs ${splitOk ? 'text-slate-500' : 'text-red-600'}`}>
                    Сумма {formatRub(splitSum)} из {formatRub(paidTotal)}
                    {splitOk ? '' : ' — должна совпасть с оплаченной'}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-outline" onClick={() => setEditing(false)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={changeMut.isPending || (mode === 'split' && !splitOk)}
                  onClick={submit}
                >
                  {changeMut.isPending ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {order.comment && (
        <div>
          <div className="text-xs text-slate-500">Комментарий</div>
          <div className="text-sm">{order.comment}</div>
        </div>
      )}
    </div>
  );
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAID: 'оплачено',
  REFUNDED: 'возврат',
  PENDING: 'ожидает',
  FAILED: 'ошибка',
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { tone: Parameters<typeof Badge>[0]['tone']; label: string }> = {
    NEW: { tone: 'info', label: 'Новый' },
    IN_PROGRESS: { tone: 'warning', label: 'В работе' },
    READY: { tone: 'success', label: 'Готов' },
    COMPLETED: { tone: 'slate', label: 'Завершён' },
    CANCELLED: { tone: 'danger', label: 'Отменён' },
  };
  const v = map[status] ?? { tone: 'slate', label: status };
  return <Badge tone={v.tone}>{v.label}</Badge>;
}
